import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, X, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { productService, categoryService } from '../services/api';
import ProductCard from '../components/products/ProductCard';

// Cache global para Products
const productsPageCache = { data: null, key: null, categories: null, totalPages: 1, totalProducts: 0, searchMeta: null };

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cacheKey = searchParams.toString();
  const hasCachedData = productsPageCache.key === cacheKey && productsPageCache.data;
  const [products, setProducts] = useState(hasCachedData ? productsPageCache.data : []);
  const [categories, setCategories] = useState(productsPageCache.categories || []);
  const [loading, setLoading] = useState(!hasCachedData);
  const [showFilters, setShowFilters] = useState(false);

  // Estado de paginación - usar searchParams para persistir la página en la URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const [totalPages, setTotalPages] = useState(hasCachedData ? productsPageCache.totalPages : 1);
  const [totalProducts, setTotalProducts] = useState(hasCachedData ? productsPageCache.totalProducts : 0);
  // Info de la búsqueda devuelta por la API (corrección "tal vez quisiste decir", resultados parciales)
  const [searchMeta, setSearchMeta] = useState(hasCachedData ? productsPageCache.searchMeta : null);
  const PRODUCTS_PER_PAGE = 20;

  // Función para cambiar de página manteniendo los demás filtros
  const setCurrentPage = (page) => {
    const newParams = new URLSearchParams(searchParams);
    if (page === 1) {
      newParams.delete('page');
    } else {
      newParams.set('page', page.toString());
    }
    setSearchParams(newParams);
  };

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    minPrice: '',
    maxPrice: '',
    sortBy: searchParams.get('sort') || '',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [searchParams]);

  const loadCategories = async () => {
    if (productsPageCache.categories) {
      setCategories(productsPageCache.categories);
      return;
    }
    try {
      const response = await categoryService.getAll();
      const cats = response.data.data || response.data;
      productsPageCache.categories = cats;
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async () => {
    const currentKey = searchParams.toString();
    // Si ya tenemos cache para estos parámetros, no recargar
    if (productsPageCache.key === currentKey && productsPageCache.data) {
      setProducts(productsPageCache.data);
      setTotalPages(productsPageCache.totalPages);
      setTotalProducts(productsPageCache.totalProducts);
      setSearchMeta(productsPageCache.searchMeta);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = {
        search: searchParams.get('search') || '',
        category: searchParams.get('category') || '',
        sort: searchParams.get('sort') || '',
        limit: PRODUCTS_PER_PAGE,
        page: currentPage,
        suggest: 1, // habilita corrección de tipeo y resultados parciales en la API
      };

      const response = await productService.getAll(params);
      const responseData = response.data;

      let productList, pages, total;

      if (responseData.data && responseData.pagination) {
        productList = responseData.data;
        pages = responseData.pagination.pages || 1;
        total = responseData.pagination.total || 0;
      } else {
        productList = responseData.data || responseData;
        pages = 1;
        total = productList.length;
      }

      // Guardar en cache
      productsPageCache.key = currentKey;
      productsPageCache.data = productList;
      productsPageCache.totalPages = pages;
      productsPageCache.totalProducts = total;
      productsPageCache.searchMeta = responseData.search_meta || null;

      setProducts(productList);
      setTotalPages(pages);
      setTotalProducts(total);
      setSearchMeta(responseData.search_meta || null);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
      setSearchMeta(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.category) params.category = filters.category;
    if (filters.sortBy) params.sort = filters.sortBy;
    // No incluir page para resetear a página 1 al aplicar filtros

    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      sortBy: '',
    });
    setSearchParams({});
    // No necesita setCurrentPage(1) porque al limpiar searchParams, page ya no existe
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll hacia arriba - usar setTimeout para asegurar que funcione en móviles
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }

      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#2d3c5d] mb-2">Productos</h1>
            <p className="text-gray-600">
              {loading ? 'Cargando...' : totalProducts > 0
                ? `Mostrando ${((currentPage - 1) * PRODUCTS_PER_PAGE) + 1}-${Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts)} de ${totalProducts} productos`
                : `${products.length} productos encontrados`
              }
            </p>
          </div>

          {/* Botón filtros móvil */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white"
          >
            <Filter className="w-5 h-5" />
            Filtros
          </button>
        </div>

        {/* Aviso de búsqueda corregida o parcial ("tal vez quisiste decir") */}
        {!loading && searchMeta && (searchMeta.type === 'corrected' || searchMeta.type === 'partial') && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-[#eddacb] bg-[#eddacb]/30 px-4 py-3 text-sm text-gray-700">
            <Search className="w-5 h-5 text-[#2d3c5d] flex-shrink-0 mt-0.5" />
            <div>
              {searchMeta.type === 'corrected' ? (
                <>
                  No encontramos resultados para <span className="font-semibold">"{searchMeta.query}"</span>.
                  {' '}Mostrando resultados para{' '}
                  <span className="font-semibold text-[#2d3c5d]">"{searchMeta.corrected_query}"</span>.
                </>
              ) : (
                <>
                  No encontramos productos con todas las palabras de{' '}
                  <span className="font-semibold">"{searchMeta.query}"</span>.
                  {' '}Mostrando resultados relacionados
                  {searchMeta.corrected_query ? (
                    <> con <span className="font-semibold text-[#2d3c5d]">"{searchMeta.corrected_query}"</span></>
                  ) : null}
                  .
                </>
              )}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar de filtros */}
          <aside
            className={`lg:block ${
              showFilters ? 'fixed inset-0 z-50 bg-white p-6 overflow-y-auto' : 'hidden'
            }`}
          >
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#2d3c5d]">Filtros</h2>
                {showFilters && (
                  <button onClick={() => setShowFilters(false)} className="lg:hidden">
                    <X className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Búsqueda */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Buscar productos..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                />
              </div>

              {/* Categorías */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ordenar */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordenar por
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d]"
                >
                  <option value="">Más relevantes</option>
                  <option value="price_asc">Menor precio</option>
                  <option value="price_desc">Mayor precio</option>
                  <option value="name_asc">Nombre A-Z</option>
                  <option value="name_desc">Nombre Z-A</option>
                  <option value="newest">Más recientes</option>
                </select>
              </div>

              {/* Botones */}
              <div className="space-y-3">
                <button
                  onClick={applyFilters}
                  className="w-full py-2.5 rounded-lg font-semibold text-white transition"
                  style={{ backgroundColor: '#2d3c5d' }}
                >
                  Aplicar filtros
                </button>
                <button
                  onClick={clearFilters}
                  className="w-full py-2.5 rounded-lg font-semibold border-2 transition"
                  style={{ borderColor: '#2d3c5d', color: '#2d3c5d' }}
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
          </aside>

          {/* Grid de productos */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
                  >
                    <div className="aspect-square bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {products.map((product, idx) => (
                    <ProductCard key={product.id} product={product} index={idx} />
                  ))}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="flex items-center gap-1">
                      {/* Botón Anterior */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Anterior</span>
                      </button>

                      {/* Números de página */}
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                              ...
                            </span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`min-w-[40px] px-3 py-2 rounded-lg font-medium transition-all ${
                                currentPage === page
                                  ? 'bg-[#2d3c5d] text-white shadow-md'
                                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>

                      {/* Botón Siguiente */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        <span className="hidden sm:inline">Siguiente</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Filter className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {searchMeta?.query
                    ? `No encontramos productos para "${searchMeta.query}"`
                    : 'No se encontraron productos'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchMeta?.query
                    ? 'Revisá la ortografía o probá con una palabra más general'
                    : 'Intentá con otros filtros o búsqueda'}
                </p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-2.5 rounded-lg font-semibold text-white transition"
                  style={{ backgroundColor: '#2d3c5d' }}
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
